/**
 * ScoreInfoBanner
 *
 * Dismissible educational banner explaining the difference between
 * ATS Score and Match Score. Appears above the ATS report in full mode.
 *
 * Dismissed state is persisted to localStorage under the key
 * 'ats-score-info-dismissed'.
 */

import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

const DISMISSED_KEY = 'ats-score-info-dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function persist(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // localStorage may be unavailable in some extension contexts
  }
}

export const ScoreInfoBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState<boolean>(isDismissed);

  if (dismissed) return null;

  const handleDismiss = () => {
    persist();
    setDismissed(true);
  };

  return (
    <div className="score-info-banner" role="note" aria-label="Score explanation">
      <Info className="score-info-banner__icon" aria-hidden="true" />
      <p className="score-info-banner__text">
        <strong className="score-info-banner__strong">ATS Score</strong> measures how well
        your CV is formatted for Applicant Tracking Systems — it checks completeness,
        structure, and impact. This is different from{' '}
        <strong className="score-info-banner__strong">Match Score</strong>, which measures
        how well your profile fits a specific job.
      </p>
      <button
        className="score-info-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss explanation"
        type="button"
      >
        <X className="score-info-banner__close-icon" aria-hidden="true" />
      </button>
    </div>
  );
};

ScoreInfoBanner.displayName = 'ScoreInfoBanner';
